import { useEffect, useState } from 'react'
import { ErrorState, LoadingState } from '../components/StateViews'
import { api } from '../lib/api'
import { relativeDate } from '../lib/format'
import { useApi } from '../lib/useApi'

type Copy = {
  subject: string
  preheader: string
  heading: string
  body: string
  buttonText: string
  logoUrl: string | null
}

type Payload = {
  locale: string
  copy: Copy
  defaults: Copy
  versions: { version: number; createdAt: number; active: boolean }[]
  editable: boolean
  tokens: string[]
}

/**
 * Email template editor with live preview and test send (SW-605).
 *
 * Test send is the single most-requested feature in this category's reviews and it is how
 * merchants self-verify during onboarding; it punches far above its weight.
 */
export function EmailTemplate() {
  const { data, error, loading, reload } = useApi<Payload>('/templates?locale=en')
  const [draft, setDraft] = useState<Copy | null>(null)
  const [preview, setPreview] = useState<string>('')
  const [testAddress, setTestAddress] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (data) setDraft({ ...data.copy })
  }, [data])

  // Preview is rendered server-side by the exact code path that sends, so what the merchant
  // sees here is what a shopper receives, not a second renderer that drifts.
  useEffect(() => {
    if (!draft) return
    const timer = setTimeout(() => {
      api
        .post<{ html: string }>('/templates/preview', draft)
        .then((result) => setPreview(result.html))
        .catch(() => setPreview(''))
    }, 400)
    return () => clearTimeout(timer)
  }, [draft])

  if (loading && !data) return <LoadingState />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data || !draft) return null

  async function save() {
    if (!draft) return
    setBusy(true)
    try {
      await api.put('/templates?locale=en', draft)
      shopify.toast.show('Template saved')
      reload()
    } catch {
      shopify.toast.show('Could not save', { isError: true })
    } finally {
      setBusy(false)
    }
  }

  async function sendTest() {
    setBusy(true)
    try {
      await api.post('/templates/test-send', { to: testAddress, copy: draft })
      shopify.toast.show(`Test sent to ${testAddress}`)
      await api.post('/onboarding/step/test_sent')
    } catch {
      shopify.toast.show('Could not send the test', { isError: true })
    } finally {
      setBusy(false)
    }
  }

  const field = (key: keyof Copy, label: string, details?: string) => (
    <s-text-field
      label={label}
      value={String(draft[key] ?? '')}
      details={details}
      onInput={(event: Event) =>
        setDraft({ ...draft, [key]: (event.target as HTMLInputElement).value })
      }
    />
  )

  return (
    <s-stack direction="block" gap="large">
      {!data.editable ? (
        <s-banner tone="info" heading="Editing needs a paid plan">
          You can preview and send tests on any plan. Customising the copy is on Starter and above.
        </s-banner>
      ) : null}

      <s-grid gridTemplateColumns="repeat(auto-fit, minmax(320px, 1fr))" gap="large">
        <s-section heading="Your copy">
          <s-stack direction="block" gap="base">
            {field(
              'subject',
              'Subject line',
              `Tokens: ${data.tokens.map((t) => `{{ ${t} }}`).join(', ')}`,
            )}
            {field('preheader', 'Preview text', 'The grey line inboxes show next to the subject.')}
            {field('heading', 'Heading')}
            <s-text-area
              label="Body"
              value={draft.body}
              onInput={(event: Event) =>
                setDraft({ ...draft, body: (event.target as HTMLTextAreaElement).value })
              }
            />
            {field(
              'buttonText',
              'Button label',
              'The button links straight to a pre-filled cart, not the product page.',
            )}
            <s-url-field
              label="Logo URL"
              value={draft.logoUrl ?? ''}
              onInput={(event: Event) =>
                setDraft({ ...draft, logoUrl: (event.target as HTMLInputElement).value || null })
              }
            />

            <s-stack direction="inline" gap="small-300">
              <s-button variant="primary" disabled={busy || !data.editable} onClick={save}>
                Save
              </s-button>
              <s-button variant="tertiary" onClick={() => setDraft({ ...data.defaults })}>
                Reset to default
              </s-button>
            </s-stack>
          </s-stack>
        </s-section>

        <s-section heading="Preview">
          <s-stack direction="block" gap="base">
            {/*
              Sandboxed. The preview is merchant-authored HTML rendered inside the merchant's
              own admin, and an unsandboxed iframe would let a stray script run in that context.
            */}
            <iframe
              title="Email preview"
              srcDoc={preview}
              sandbox=""
              style={{
                width: '100%',
                height: '520px',
                border: '1px solid #e3e3e3',
                borderRadius: '8px',
                background: '#fff',
              }}
            />
            <s-stack direction="inline" gap="small-300" alignItems="end">
              <s-email-field
                label="Send a test to"
                value={testAddress}
                onInput={(event: Event) => setTestAddress((event.target as HTMLInputElement).value)}
              />
              <s-button disabled={busy || !testAddress} onClick={sendTest}>
                Send test
              </s-button>
            </s-stack>
          </s-stack>
        </s-section>
      </s-grid>

      {data.versions.length > 1 ? (
        <s-section heading="Version history">
          <s-table>
            <s-table-header-row>
              <s-table-header>Version</s-table-header>
              <s-table-header>Saved</s-table-header>
              <s-table-header />
            </s-table-header-row>
            <s-table-body>
              {data.versions.map((version) => (
                <s-table-row key={version.version}>
                  <s-table-cell>
                    v{version.version}{' '}
                    {version.active ? <s-badge tone="success">Live</s-badge> : null}
                  </s-table-cell>
                  <s-table-cell>{relativeDate(version.createdAt)}</s-table-cell>
                  <s-table-cell>
                    {version.active ? null : (
                      <s-button
                        variant="tertiary"
                        onClick={async () => {
                          await api.post(`/templates/activate?locale=en&version=${version.version}`)
                          shopify.toast.show(`Rolled back to v${version.version}`)
                          reload()
                        }}
                      >
                        Restore
                      </s-button>
                    )}
                  </s-table-cell>
                </s-table-row>
              ))}
            </s-table-body>
          </s-table>
        </s-section>
      ) : null}
    </s-stack>
  )
}
